import { PERSIAN_SHORT_CONNECTORS } from '@/constants'

// Zero Width Joiner - forces connected letter forms via OpenType shaping
const ZWJ = '\u200D'

// Letters that can only connect to the right (previous letter in RTL)
// These letters don't have initial or medial forms - only isolated and final
const RIGHT_JOINING_ONLY = new Set([
  'ا', 'آ', 'أ', 'إ', 'ؤ', 'ء', 'د', 'ذ', 'ر', 'ز', 'ژ', 'و',
])

/**
 * Determines if a character is a Persian/Arabic letter
 */
function isArabicLetter(char: string): boolean {
  const code = char.charCodeAt(0)
  return (code >= 0x0600 && code <= 0x06FF) || (code >= 0x0750 && code <= 0x077F)
}

/**
 * Get the letter with ZWJ characters to trigger OpenType contextual forms
 *
 * ZWJ (Zero Width Joiner) triggers the font's OpenType shaping:
 * - Initial form: letter + ZWJ (connects to left/next)
 * - Medial form: ZWJ + letter + ZWJ (connects both sides)
 * - Final form: ZWJ + letter (connects to right/previous)
 * - Isolated: just the letter
 */
export function getLetterWithForm(
  letter: string,
  letterIndex: number,
  wordLength: number,
  wordText: string
): string {
  // Only process Arabic/Persian letters
  if (!isArabicLetter(letter)) {
    return letter
  }

  const isFirst = letterIndex === 0
  const isLast = letterIndex === wordLength - 1
  const isRightJoiningOnly = RIGHT_JOINING_ONLY.has(letter)

  // Check adjacent letters
  const prevChar = letterIndex > 0 ? wordText[letterIndex - 1] : null
  const nextChar = letterIndex < wordLength - 1 ? wordText[letterIndex + 1] : null

  // Determine connections
  // Connect to right (previous in string) if previous char exists and is Arabic
  // But if previous char is right-joining-only, it doesn't connect to us on its left
  const prevCanConnectLeft = prevChar && isArabicLetter(prevChar) && !RIGHT_JOINING_ONLY.has(prevChar)
  const connectsRight = !isFirst && prevChar && isArabicLetter(prevChar) && prevCanConnectLeft

  // Connect to left (next in string) if next char exists, is Arabic, and we can connect left
  const connectsLeft = !isLast && nextChar && isArabicLetter(nextChar) && !isRightJoiningOnly

  // Build the string with ZWJ to trigger correct form
  let result = letter

  if (connectsRight) {
    // Add ZWJ before to indicate connection to right (previous letter)
    result = ZWJ + result
  }

  if (connectsLeft) {
    // Add ZWJ after to indicate connection to left (next letter)
    result = result + ZWJ
  }

  return result
}

interface WordMeasurement {
  word: string
  width: number
  length: number
  isConnector: boolean
}

interface SpacingResult {
  measurements: WordMeasurement[]
  spacings: number[]
  totalWidth: number
}

interface TextMeasurementContext {
  measureText: (text: string) => TextMetrics
}

/**
 * Calculate intelligent word spacing for Persian/Nastaliq typography
 * Handles short connector words (که، از، به، etc.) with tight spacing
 */
export function calculateIntelligentSpacing(
  words: string[],
  ctx: TextMeasurementContext,
  fontSize: number,
  wordSpacingMultiplier: number
): SpacingResult {
  const measurements: WordMeasurement[] = []
  const spacings: number[] = []

  // Measure each word
  for (const word of words) {
    const metrics = ctx.measureText(word)
    const actualWidth = metrics.width

    // Get visual bounds if available
    let visualWidth = actualWidth
    if (
      metrics.actualBoundingBoxLeft !== undefined &&
      metrics.actualBoundingBoxRight !== undefined
    ) {
      visualWidth = metrics.actualBoundingBoxLeft + metrics.actualBoundingBoxRight
    }

    const effectiveWidth = Math.max(actualWidth, visualWidth)

    // Check if it's a short connector word
    const isConnector = PERSIAN_SHORT_CONNECTORS.includes(word)

    measurements.push({
      word: word,
      width: effectiveWidth,
      length: word.length,
      isConnector: isConnector,
    })
  }

  // Base gap controlled by user
  const baseGap = fontSize * wordSpacingMultiplier

  // Calculate spacing with visual compensation
  let totalWidth = 0
  for (let i = 0; i < words.length; i++) {
    totalWidth += measurements[i].width

    if (i < words.length - 1) {
      const currentWord = measurements[i]
      const nextWord = measurements[i + 1]

      // Start with base gap
      let adjustedGap = baseGap

      // Rule 1: Short connector words get MINIMAL spacing
      // که، از، به etc. should almost touch adjacent words
      const isConnectorBoundary = currentWord.isConnector || nextWord.isConnector

      if (currentWord.isConnector && nextWord.isConnector) {
        // Both are connectors - almost no gap
        adjustedGap = fontSize * 0.01
      } else if (isConnectorBoundary) {
        // One side is connector - minimal gap
        adjustedGap = fontSize * 0.02
      }

      // Rule 2: Very short words (1-2 chars) get tighter spacing
      if (!isConnectorBoundary) {
        if (currentWord.length <= 2 || nextWord.length <= 2) {
          adjustedGap *= 0.6
        }
      }

      // Rule 3: Clamp to reasonable bounds
      const minGap = fontSize * 0.005 // Nearly zero
      const maxGap = isConnectorBoundary ? fontSize * 0.03 : fontSize * 0.25
      adjustedGap = Math.max(adjustedGap, minGap)
      adjustedGap = Math.min(adjustedGap, maxGap)

      spacings.push(adjustedGap)
      totalWidth += adjustedGap
    } else {
      spacings.push(0)
    }
  }

  return { measurements, spacings, totalWidth }
}
